"use client";

import Link from "next/link";
import { useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import type { Locale } from "@/lib/i18n";
import {
  MODULE_GROUPS,
  MODULE_ICON_PATHS,
  moduleSlug,
  modulePath,
  type ModuleIconKey,
} from "@/lib/modules-data";

const easeOut = [0.22, 1, 0.36, 1] as const;

function modulesHref(locale: Locale) {
  return locale === "en" ? "/en/modules" : "/moduller";
}
function groupHref(locale: Locale, key: string) {
  return `${modulesHref(locale)}#${key}`;
}

function Icon({ name }: { name: ModuleIconKey }) {
  return (
    <svg
      className="h-5 w-5 shrink-0"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={MODULE_ICON_PATHS[name]} />
    </svg>
  );
}

/** Masaüstü "Modüller" mega-menüsü (grup listesi + aktif grubun modülleri). */
export function NavModules({ locale }: { locale: Locale }) {
  const prefersReducedMotion = useReducedMotion();
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | undefined>(undefined);

  const label = locale === "en" ? "Modules" : "Modüller";
  const total = MODULE_GROUPS.reduce((n, g) => n + g.modules.length, 0);

  const openNow = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const closeSoon = () => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 130);
  };

  return (
    <div className="relative shrink-0" onMouseEnter={openNow} onMouseLeave={closeSoon}>
      <Link
        href={modulesHref(locale)}
        onClick={() => setOpen(false)}
        onFocus={openNow}
        aria-expanded={open}
        aria-haspopup="true"
        className="relative flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-3 text-[15px] font-light tracking-normal text-[#172554] transition-[letter-spacing,font-weight] duration-300 ease-out hover:font-medium hover:tracking-wide motion-reduce:hover:font-light motion-reduce:hover:tracking-normal dark:text-white cloud:text-[#4338ca] cloud:hover:text-[#6157e1] lg:px-3 2xl:px-2.5 2xl:text-base"
      >
        {label}
        <svg
          className={`h-4 w-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
        </svg>
      </Link>

      <AnimatePresence>
        {open ? (
          <motion.div
            role="menu"
            initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
            transition={{ duration: 0.2, ease: easeOut }}
            className="absolute left-1/2 top-full z-50 mt-3 w-[min(94vw,900px)] -translate-x-1/2 overflow-hidden rounded-2xl border border-border bg-background p-5 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)]"
          >
            <div className="gap-x-7 sm:columns-3">
              {MODULE_GROUPS.map((g) => (
                <div key={g.key} className="mb-5 break-inside-avoid">
                  <Link
                    href={groupHref(locale, g.key)}
                    onClick={() => setOpen(false)}
                    className="mb-2 flex items-center gap-2.5"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-foreground">
                      <Icon name={g.icon} />
                    </span>
                    <span className="text-sm font-semibold text-foreground">{g.title[locale]}</span>
                  </Link>
                  <ul className="space-y-0.5 pl-0.5">
                    {g.modules.map((m) => (
                      <li key={m.name.tr}>
                        <Link
                          href={modulePath(locale, moduleSlug(m, locale))}
                          onClick={() => setOpen(false)}
                          className="block rounded-md px-1.5 py-1 text-[13px] text-muted transition-colors hover:bg-card hover:text-foreground"
                        >
                          {m.name[locale]}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-1 flex items-center justify-between border-t border-border px-1.5 pt-3">
              <p className="text-xs text-muted">
                {locale === "en"
                  ? `${total} modules · core + add-ons`
                  : `${total} modül · çekirdek + ek modüller`}
              </p>
              <Link
                href={modulesHref(locale)}
                onClick={() => setOpen(false)}
                className="inline-flex items-center gap-1 text-sm font-medium text-[#F97316] underline-offset-4 hover:underline"
              >
                {locale === "en" ? "All modules" : "Tüm modüller"} →
              </Link>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}

/** Mobil menü içinde açılır "Modüller" bölümü */
export function NavModulesMobile({
  locale,
  onNavigate,
}: {
  locale: Locale;
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const label = locale === "en" ? "Modules" : "Modüller";

  return (
    <div className="border-t border-border/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between rounded-xl px-3 py-4 text-lg font-light text-[#172554]/85 dark:text-white/85 cloud:text-[#4338ca]"
      >
        {label}
        <svg
          className={`h-5 w-5 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <ul className="space-y-1 pb-2">
          {MODULE_GROUPS.map((g) => (
            <li key={g.key}>
              <Link
                href={groupHref(locale, g.key)}
                onClick={onNavigate}
                className="flex items-start gap-3 rounded-xl px-3 py-2.5"
              >
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-border text-foreground">
                  <Icon name={g.icon} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-foreground">
                    {g.title[locale]}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted">
                    {g.modules.map((m) => m.name[locale]).join(" · ")}
                  </span>
                </span>
              </Link>
            </li>
          ))}
          <li className="px-3 pt-1">
            <Link
              href={modulesHref(locale)}
              onClick={onNavigate}
              className="text-sm font-medium text-foreground underline underline-offset-4"
            >
              {locale === "en" ? "All modules" : "Tüm modüller"} →
            </Link>
          </li>
        </ul>
      ) : null}
    </div>
  );
}
